/**
 * The lobby's FORM state (M2 spec §2) — the one store `pages/home` is allowed
 * to add.
 *
 * WHY a store and not React state: the old panel kept every selection in
 * `GameSearch`'s own `useState` (index.tsx L84-108), so a keystroke in the FEN
 * field re-rendered four game-option tiles with their Rive canvases, six
 * time-control tiles with their Lottie players, three dropdowns and the CTA.
 * Here each tile subscribes to ONE boolean — `selected` for ITS value — so a
 * selection change re-renders exactly the two tiles that swapped.
 *
 * Persistence, all of it through `platform/storage` (spec §5.1):
 *  - `tcMs` / `incMs` ← the app store's `lastGameTimeControl`, the key
 *    PlayChess already writes on every game start (async, hence `hydrated`);
 *  - `tab` / `option` ← the synchronous pref `cc_lobby_ui`, a per-viewer
 *    convenience so the panel opens where it was left.
 * Everything else (fen, colour, opponent, library) resets on mount, exactly as
 * the old component's `useState` defaults did.
 */
import {create} from 'zustand';

import {getItem, getPrefSync, setPref} from '@/Store/liveGame/platform/storage';

import {isOfferedTimeControl} from './models/timeControls';

export type LobbyTab = 'play' | 'learn' | 'tournaments';
/** GameSearch's `selectedGameOption` (index.tsx L99-104). */
export type GameOptionValue = '0' | '1' | '2' | '3';
/** CustomConfig L57-60: 1 white, 0 black, -1 random. */
export type LobbyColor = 1 | 0 | -1;

/** the UI memory key — `{tab, option}`, nothing a server cares about */
export const LOBBY_UI_KEY = 'cc_lobby_ui';
/** the key PlayChess writes on game start, and the old panel read back */
export const LAST_TC_KEY = 'lastGameTimeControl';

/** 5+3 is the product default (GameSearch L44 `DEFAULT_CONTROL`). */
export const DEFAULT_TC_MS = 300000;
export const DEFAULT_INC_MS = 3000;

/**
 * GameSearch's own default is option '1' (L91 and L198-203: both branches of
 * the league check set '1'), so the panel opens on "Play a CircleChess user".
 */
export const DEFAULT_OPTION: GameOptionValue = '1';

export type LobbyUiState = {
  tab: LobbyTab;
  option: GameOptionValue;
  tcMs: number;
  incMs: number;
  showCustomConfig: boolean;
  /** the last FEN that PARSED; an invalid one is never committed (L41-50) */
  fen: string;
  fenError: string;
  color: LobbyColor;
  /** '' | '<userId>:<username>' | '0:Caissa <rating>' (ChallengeCaissa L144) */
  opponent: string;
  libraryStage: string;
  libraryTag: string;
  /** the async read of `lastGameTimeControl` has settled */
  hydrated: boolean;
  setTab: (tab: LobbyTab) => void;
  setOption: (option: GameOptionValue) => void;
  setTimeControl: (tcMs: number, incMs: number) => void;
  toggleCustomConfig: () => void;
  setFen: (fen: string) => void;
  setFenError: (fenError: string) => void;
  setColor: (color: LobbyColor) => void;
  setOpponent: (opponent: string) => void;
  setLibraryStage: (libraryStage: string) => void;
  setLibraryTag: (libraryTag: string) => void;
  /** read the persisted picks; safe to call on every mount */
  hydrate: () => Promise<void>;
};

type StoredUi = {tab?: string; option?: string};

const OPTIONS: GameOptionValue[] = ['0', '1', '2', '3'];

const readStoredUi = (): StoredUi => {
  const raw = getPrefSync(LOBBY_UI_KEY);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeStoredUi = (tab: LobbyTab, option: GameOptionValue): void => {
  setPref(LOBBY_UI_KEY, JSON.stringify({tab, option}));
};

/**
 * The lobby never opens on the Tournaments tab from memory: a viewer who
 * happened to leave the panel there would otherwise never see the Play tab
 * again without pressing it (the old panel always opened on Play, L133-143).
 */
const initialTab = (): LobbyTab => {
  const stored = readStoredUi().tab;
  return stored === 'learn' ? 'learn' : 'play';
};

const initialOption = (): GameOptionValue => {
  const stored = readStoredUi().option;
  return OPTIONS.includes(stored as GameOptionValue)
    ? (stored as GameOptionValue)
    : DEFAULT_OPTION;
};

export const START_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/** the blank form, minus the two persisted picks (GameSearch L84-108). */
const blank = {
  showCustomConfig: false,
  fen: START_FEN,
  fenError: '',
  // CustomConfig's `selectedColor` starts on Random (L67-70), which is what
  // GameSearch turns into an empty `white` (L325).
  color: -1 as LobbyColor,
  opponent: '',
  libraryStage: '',
  libraryTag: '',
};

export const useLobby = create<LobbyUiState>(set => ({
  tab: initialTab(),
  option: initialOption(),
  tcMs: DEFAULT_TC_MS,
  incMs: DEFAULT_INC_MS,
  hydrated: false,
  ...blank,

  setTab: tab =>
    set(s => {
      if (s.tab === tab) {
        return s;
      }
      writeStoredUi(tab, s.option);
      // fix Z3: defensive, one line — whatever left `tcMs`/`incMs` on
      // something this lobby does not offer (a stale `lastGameTimeControl`
      // from Play & Learn's fixed 60+15, or any future bug), the Play tab
      // must never show six unselected chips.
      return {
        ...s,
        tab,
        ...(tab === 'play' && !isOfferedTimeControl(s.tcMs, s.incMs)
          ? {tcMs: DEFAULT_TC_MS, incMs: DEFAULT_INC_MS}
          : null),
      };
    }),

  setOption: option =>
    set(s => {
      if (s.option === option) {
        return s;
      }
      writeStoredUi(s.tab, option);
      // the old tile clears the opponent on every option change (L617-620),
      // because an opponent picked for Caissa is meaningless for a friend
      return {...s, option, opponent: ''};
    }),

  setTimeControl: (tcMs, incMs) =>
    set(s => (s.tcMs === tcMs && s.incMs === incMs ? s : {...s, tcMs, incMs})),

  toggleCustomConfig: () =>
    set(s => ({...s, showCustomConfig: !s.showCustomConfig})),

  setFen: fen => set(s => (s.fen === fen ? s : {...s, fen})),
  setFenError: fenError =>
    set(s => (s.fenError === fenError ? s : {...s, fenError})),
  setColor: color => set(s => (s.color === color ? s : {...s, color})),
  setOpponent: opponent =>
    set(s => (s.opponent === opponent ? s : {...s, opponent})),
  setLibraryStage: libraryStage =>
    set(s => (s.libraryStage === libraryStage ? s : {...s, libraryStage})),
  setLibraryTag: libraryTag =>
    set(s => (s.libraryTag === libraryTag ? s : {...s, libraryTag})),

  /**
   * Two sources, in order: 5+3 (the old panel's `DEFAULT_CONTROL`, GameSearch
   * L44 — it always opened there, its `lastTimecontrol` prop is destructured at
   * L120 and never read) and then `lastGameTimeControl`, the key PlayChess
   * writes on every game start and the spec asks the lobby to honour (§2).
   *
   * Deliberately NOT seeded from the live `lobby` slice: its initial value is
   * 3+0, which is not a control this lobby offers, so a brand-new viewer would
   * see no tile selected and a Play press would start a 3+0 game. The slice was
   * only ever a proxy for "the control just played", and after a game
   * `useGameplayEffects` writes that to `lastGameTimeControl` anyway — so the
   * observable behaviour is the same on every real path.
   *
   * Re-run on every mount on purpose: after a game ends, "New game" remounts
   * the page and the picker then shows the control that was just played.
   *
   * fix Z3: adopted only when it is one of the six offered controls
   * (`isOfferedTimeControl`) — `useGameplayEffects` writes this key on EVERY
   * game including Play & Learn's fixed 60+15, which is the mode's control,
   * never a lobby preference (see that hook's own fix Z3 note). A miss is a
   * NO-OP (same shape as the original `tcMs > 0` guard below), which "keeps
   * the default" because `tcMs`/`incMs` already start on 5+3 above — and,
   * unlike an unconditional reset, does not clobber a control a caller set
   * before this async read settles. The OLD lobby's own fallback on a miss
   * was the FIRST chip (2+0, GameSearch L224-233); this lane's default stays
   * 5+3 instead — the owner can change that initial value in one line.
   */
  hydrate: async () => {
    const stored = await getItem<any>(LAST_TC_KEY);
    const tcMs = Number(stored?.moveTimer);
    const incMs = Number(stored?.Increment);
    const normalisedIncMs = Number.isFinite(incMs) ? incMs : 0;
    const offered =
      Number.isFinite(tcMs) &&
      tcMs > 0 &&
      isOfferedTimeControl(tcMs, normalisedIncMs);
    set(s => ({
      ...s,
      ...(offered ? {tcMs, incMs: normalisedIncMs} : null),
      hydrated: true,
    }));
  },
}));

/**
 * Back to the blank form. Exported for the design lab; the two persisted picks
 * are deliberately NOT reset (they are the viewer's memory).
 */
export const resetLobbyForm = (): void => {
  useLobby.setState(s => ({...s, ...blank}));
};

/**
 * The whole slice back to first-load state, persisted picks included. For TESTS
 * only: the store is a module singleton, so one suite's picks would otherwise
 * leak into the next.
 */
export const resetLobbyUiForTest = (): void => {
  useLobby.setState(s => ({
    ...s,
    ...blank,
    tab: 'play',
    option: DEFAULT_OPTION,
    tcMs: DEFAULT_TC_MS,
    incMs: DEFAULT_INC_MS,
    hydrated: false,
  }));
};

export default useLobby;
